/**
 * ShallowWaterGpu — the rain-puddle PATCH: the virtual-pipes shallow-water model (Mei, Decaudin & Hu, "Fast Hydraulic
 * Erosion Simulation and Visualization on GPU", PG 2007) as TSL compute on WebGPU (shader gaps R6, 2026-09-30).
 *
 * THE ORACLE IS THE CPU SOLVER. `ShallowWaterSolver` in patch mode (`patch: true`) is this, line for line: kernel K1
 * (the pipes — per face max(0, f·keep + g·A/l·Δhead·Δt), Dagenais et al. 2018's implicit viscosity H²/(H² + 3·Δt·ν),
 * a solid cell closes every pipe touching it, an edge face runs against the terrain beyond it (`edgeBed`) or is
 * closed, then Mei's outflow limiter K = min(1, d·A / (Σf·Δt))) and kernel K2 (the column: Σin − Σout, the clamp,
 * then rain in, infiltration, evaporation and drains — every term into a per-cell LEDGER). Both kernels are parallel-
 * safe exactly as the CPU loops are (K1 reads depths and its own old fluxes; K2 reads the new fluxes). `emulateSubstep`
 * below runs the kernels' arithmetic in float32 on the CPU — the vitest proves it agrees with the oracle; the bed
 * (Atlas `puddle_probe.html`) proves the real GPU agrees with both.
 *
 * THE STEP. Lumped pipes (A = l², Šťava 2008 / Dagenais 2018) carry a gravity wave at √(g·l) whatever the depth, so
 * the CFL step is a constant of the grid (`ShallowWaterSolver.cflStep`); the patch integrates FIXED steps of
 * min(cfl, maxStep), carries the remainder, clamps the debt at max(hitch, 2 steps) and runs at most `maxSubsteps` a
 * frame (what is left is dropped — a hitch loses sim time, it can never freeze the patch: the RippleField law).
 *
 * THE WINDOW FOLLOWS THE CAMERA by WHOLE cells (`shift`, RippleField.setOrigin's pattern): three kernels gather the
 * state at the new offset into a scratch pair (cells that leave are ledgered as shifted out) and copy it back; the
 * owner re-uploads the bed / sources / edges for the new window (the CPU keeps those — they come from the drawn ground).
 *
 * THE LOOK. K3 writes one vec4 per cell for renderers: (depth m, the water LEVEL bed + depth relative to the owner's
 * refY, the WATER-surface slope over wet neighbours, valid 0|1 — 0 on a pending cell) — `lookNode()` hands a
 * read-only storage node a fragment shader can index (a storage buffer on WebGPU counts against storage buffers, never
 * against the sampler budget).
 *
 * NEVER AUTHORITATIVE: nothing gameplay reads this water — no buoyancy, no swimming, no server. It is a picture.
 */

import * as THREE from 'three/webgpu';
import { Fn, If, abs, float, instanceIndex, int, max, min, select, smoothstep, storage, uniform, vec3, vec4 } from 'three/tsl';
import { ShallowWaterSolver } from './ShallowWaterSolver.js';

// TSL node graphs are loosely typed (@types/three 0.182)
type N = any;

export interface ShallowWaterGpuConfig {
  /** Cells per side (N×N). A multiple of 8 (whole 64-thread workgroups). */
  resolution: number;
  /** Cell spacing (m). */
  cellSize: number;
  /** Pipe damping 1/s (the oracle's `damping`). Default 0.5. */
  damping?: number;
  /** Kinematic viscosity (m²/s) — Dagenais' implicit factor. Default 1e-5 (the paper's realistic water range). */
  viscosity?: number;
  gravity?: number;
  /** CFL safety factor. Default 0.5. */
  courant?: number;
  /** The step's cap (s). Default 1/20. */
  maxStep?: number;
  /** Steps per `advance` at most. Default 6. */
  maxSubsteps?: number;
  /** The time-debt clamp (s) — raised to ≥ 2 steps. Default 0.25. */
  maxDebt?: number;
  /** Edges open against `edgeBed` (true) or closed walls. Default true. */
  openEdges?: boolean;
  /** The sheet drainage's fade over the water-surface slope (the oracle's `sheetSlope`). Default [0.005, 0.02]. */
  sheetSlope?: [number, number];
  /** Write the look into this buffer (a renderer built its storage node before the patch existed). n²·4 floats. */
  lookAttr?: THREE.StorageBufferAttribute;
}

/** One window of the ledger, summed on the CPU (m³). */
export interface ShallowWaterGpuLedger { rain: number; infiltration: number; evaporation: number; runoff: number; drained: number; boundary: number; clampGain: number; shiftedOut: number }

export interface ShallowWaterGpuReadback { depth: Float32Array; volume: number; wetCells: number; maxDepth: number; ledger: ShallowWaterGpuLedger }

interface ComputeRenderer {
  compute(nodes: unknown): unknown;
  getArrayBufferAsync(attr: unknown): Promise<ArrayBuffer>;
}

/** THE WARM's stages (`prime`): the mask kernel's stage, and the count. */
const PRIME_MASK = 6;
const PRIME_DONE = 10;

/** The fixed step a patch integrates with: min(CFL, maxStep). */
export function patchStep(cellSize: number, gravity = 9.81, courant = 0.5, maxStep = 1 / 20): number {
  return Math.min(ShallowWaterSolver.cflStep(cellSize, gravity, courant), maxStep);
}

export class ShallowWaterGpu {
  readonly n: number;
  readonly cell: number;
  readonly dt: number;
  readonly damping: number;
  readonly viscosity: number;
  readonly gravity: number;
  readonly maxSubsteps: number;
  readonly maxDebt: number;
  readonly openEdges: boolean;

  // ── CPU-owned inputs (the owner writes, then `markInputsDirty`) ──
  /** Bed height per cell, RELATIVE to `refY` (keeps f32 precision: a patch spans metres, not the world's hundreds). */
  readonly bed: Float32Array;
  /** Per cell (infiltration capacity m/s, rain exposure 0..1, kind: 0 ground | 1 solid | 2 drain | 3 pending, retention m).
   *  PENDING (3): a cell whose bed the owner has not built yet — closed like a solid, and the look's `w` is 0 there (a
   *  renderer falls back to whatever it drew before the patch). */
  readonly src: Float32Array;
  /** Beds one cell beyond the edges (relative to refY): +x by row, −x by row, +z by column, −z by column. */
  readonly edge: Float32Array;

  // ── GPU state ──
  readonly bedAttr: THREE.StorageBufferAttribute;
  readonly srcAttr: THREE.StorageBufferAttribute;
  readonly edgeAttr: THREE.StorageBufferAttribute;
  readonly depthAttr: THREE.StorageBufferAttribute;
  readonly fluxAttr: THREE.StorageBufferAttribute;
  readonly ledgerAttr: THREE.StorageBufferAttribute;
  readonly ledger2Attr: THREE.StorageBufferAttribute;
  readonly lookAttr: THREE.StorageBufferAttribute;
  /** The pre-step water-surface slope per cell (K1 writes, K2's sheet fade reads). */
  private readonly slopeAttr: THREE.StorageBufferAttribute;
  private readonly tmpDepthAttr: THREE.StorageBufferAttribute;
  private readonly tmpFluxAttr: THREE.StorageBufferAttribute;

  // ── uniforms ──
  /** The open-sky rain rate (m/s) — a cell gets uRain × its exposure. */
  readonly uRain = uniform(0);
  /** Evaporation (m/s). */
  readonly uEvap = uniform(0);
  /** A multiplier on every cell's infiltration capacity (the wet clock's pore room). */
  readonly uInfilScale = uniform(1);
  /** Sheet drainage (m/s): a stagnant sheet above its cell's retention leaves at up to this (the owner passes the rain rate). */
  readonly uSheetDrain = uniform(0);
  private readonly uShift = uniform(new THREE.Vector2(0, 0));

  private readonly kFlux: N;
  private readonly kDepth: N;
  private readonly kLook: N;
  private readonly kShiftGather: N;
  private readonly kShiftLost: N;
  private readonly kShiftCopy: N;
  private readonly kLedgerReset: N;
  private readonly kClear: N;
  private maskKernel: N = null;

  private debt = 0;
  /** Substeps dispatched since construction; frames that stepped; the last frame's substep count. */
  readonly stats = { substeps: 0, frames: 0, lastSubsteps: 0, dropped: 0, shifts: 0, uploads: 0, primed: 0 };
  private inputsDirty = true;

  constructor(cfg: ShallowWaterGpuConfig) {
    const n = Math.max(8, Math.round(cfg.resolution / 8) * 8);
    this.n = n;
    this.cell = cfg.cellSize;
    this.gravity = cfg.gravity ?? 9.81;
    this.damping = Math.max(0, cfg.damping ?? 0.5);
    this.viscosity = Math.max(0, cfg.viscosity ?? 1e-5);
    this.dt = patchStep(this.cell, this.gravity, cfg.courant ?? 0.5, cfg.maxStep ?? 1 / 20);
    this.maxSubsteps = Math.max(1, Math.floor(cfg.maxSubsteps ?? 6));
    this.maxDebt = Math.max(cfg.maxDebt ?? 0.25, 2 * this.dt);
    this.openEdges = cfg.openEdges ?? true;
    const nn = n * n;
    this.bed = new Float32Array(nn);
    this.src = new Float32Array(nn * 4);
    this.edge = new Float32Array(4 * n);
    this.bedAttr = new THREE.StorageBufferAttribute(this.bed, 1);
    this.srcAttr = new THREE.StorageBufferAttribute(this.src, 4);
    this.edgeAttr = new THREE.StorageBufferAttribute(this.edge, 1);
    this.depthAttr = new THREE.StorageBufferAttribute(new Float32Array(nn), 1);
    this.fluxAttr = new THREE.StorageBufferAttribute(new Float32Array(nn * 4), 4);
    this.ledgerAttr = new THREE.StorageBufferAttribute(new Float32Array(nn * 4), 4);
    this.ledger2Attr = new THREE.StorageBufferAttribute(new Float32Array(nn * 4), 4);
    this.lookAttr = cfg.lookAttr && cfg.lookAttr.array.length === nn * 4 ? cfg.lookAttr : new THREE.StorageBufferAttribute(new Float32Array(nn * 4), 4);
    this.slopeAttr = new THREE.StorageBufferAttribute(new Float32Array(nn), 1);
    this.tmpDepthAttr = new THREE.StorageBufferAttribute(new Float32Array(nn), 1);
    this.tmpFluxAttr = new THREE.StorageBufferAttribute(new Float32Array(nn * 4), 4);
    for (let i = 0; i < nn; i++) this.src[i * 4 + 1] = 1; // exposed until a mask says otherwise

    const bedS: N = storage(this.bedAttr, 'float', nn).toReadOnly();
    const srcS: N = storage(this.srcAttr, 'vec4', nn);
    const edgeS: N = storage(this.edgeAttr, 'float', 4 * n).toReadOnly();
    const depthS: N = storage(this.depthAttr, 'float', nn);
    const fluxS: N = storage(this.fluxAttr, 'vec4', nn);
    const ledS: N = storage(this.ledgerAttr, 'vec4', nn);
    const led2S: N = storage(this.ledger2Attr, 'vec4', nn);
    const lookS: N = storage(this.lookAttr, 'vec4', nn);
    const tmpDS: N = storage(this.tmpDepthAttr, 'float', nn);
    const tmpFS: N = storage(this.tmpFluxAttr, 'vec4', nn);
    const slopeS: N = storage(this.slopeAttr, 'float', nn);
    const [sLo, sHi] = cfg.sheetSlope ?? [0.005, 0.02];
    // closed = a solid (1) or a pending cell (3); a drain is 2
    const isSolid = (k: N): N => k.greaterThan(0.5).and(k.lessThan(1.5).or(k.greaterThan(2.5)));
    const inv2c = 0.5 / this.cell;

    const A = this.cell * this.cell;
    const pipeK = (this.gravity * A) / this.cell;
    const keep = Math.max(0, 1 - this.damping * this.dt);
    const visc3 = 3 * this.dt * this.viscosity;
    const dt = this.dt;
    const open = this.openEdges;
    const last = n - 1;

    // ── K1: the pipes ──
    this.kFlux = Fn(() => {
      const i: N = int(instanceIndex);
      const col: N = i.mod(n), row: N = i.div(n);
      const d: N = depthS.element(i);
      const S: N = srcS.element(i);
      const fOld: N = fluxS.element(i);
      const out: N = vec4(0, 0, 0, 0).toVar('swOut');
      const sl: N = float(0).toVar('swSl');
      If(isSolid(S.z).not(), () => {
        const head: N = bedS.element(i).add(d).toVar('swHead');
        // the pre-step water-surface slope over WET, open neighbours (the oracle's etaOf)
        const etaOf = (ok: N, j: N): N => select(ok.and(depthS.element(j).greaterThan(1e-4)).and(isSolid(srcS.element(j).z).not()), bedS.element(j).add(depthS.element(j)), head);
        const jE: N = min(i.add(1), int(nn - 1)), jW: N = max(i.sub(1), int(0)), jS: N = min(i.add(n), int(nn - 1)), jN: N = max(i.sub(n), int(0));
        const gx: N = etaOf(col.lessThan(last), jE).sub(etaOf(col.greaterThan(0), jW)).mul(inv2c);
        const gz: N = etaOf(row.lessThan(last), jS).sub(etaOf(row.greaterThan(0), jN)).mul(inv2c);
        sl.assign(max(abs(gx), abs(gz)));
        const vis: N = visc3 > 0 ? d.mul(d).div(d.mul(d).add(visc3)) : float(1);
        const face = (s: 0 | 1 | 2 | 3, fPrev: N): N => {
          const inside: N = s === 0 ? col.lessThan(last) : s === 1 ? col.greaterThan(0) : s === 2 ? row.lessThan(last) : row.greaterThan(0);
          const nc: N = s === 0 ? min(col.add(1), int(last)) : s === 1 ? max(col.sub(1), int(0)) : col;
          const nr: N = s === 2 ? min(row.add(1), int(last)) : s === 3 ? max(row.sub(1), int(0)) : row;
          const j: N = nr.mul(n).add(nc);
          const eIdx: N = s === 0 ? row : s === 1 ? row.add(n) : s === 2 ? col.add(2 * n) : col.add(3 * n);
          const headJ: N = bedS.element(j).add(depthS.element(j));
          const ghost: N = edgeS.element(eIdx);
          const headN: N = select(inside, headJ, ghost);
          const raw: N = max(float(0), fPrev.mul(keep).add(float(pipeK).mul(head.sub(headN)).mul(dt))).mul(vis);
          const solidJ: N = isSolid(srcS.element(j).z);
          return select(inside, select(solidJ, float(0), raw), open ? raw : float(0));
        };
        const f0: N = face(0, fOld.x).toVar('swF0'), f1: N = face(1, fOld.y).toVar('swF1');
        const f2: N = face(2, fOld.z).toVar('swF2'), f3: N = face(3, fOld.w).toVar('swF3');
        const total: N = f0.add(f1).add(f2).add(f3);
        const k: N = select(total.greaterThan(0), min(float(1), d.mul(A).div(total.mul(dt))), float(1));
        out.assign(vec4(f0, f1, f2, f3).mul(k));
      });
      fluxS.element(i).assign(out);
      slopeS.element(i).assign(sl);
    })().compute(nn);

    // ── K2: the column and its sources / sinks (ledgered per cell, in metres) ──
    this.kDepth = Fn(() => {
      const i: N = int(instanceIndex);
      const col: N = i.mod(n), row: N = i.div(n);
      const S: N = srcS.element(i);
      const d0: N = depthS.element(i).toVar('swD0');
      If(isSolid(S.z), () => {
        // a cell a building stands on holds no water (displaced — drained)
        ledS.element(i).assign(ledS.element(i).add(vec4(0, 0, 0, d0)));
        depthS.element(i).assign(float(0));
      }).Else(() => {
        const f: N = fluxS.element(i);
        const inE: N = select(col.lessThan(last), fluxS.element(min(i.add(1), int(nn - 1))).y, float(0));
        const inW: N = select(col.greaterThan(0), fluxS.element(max(i.sub(1), int(0))).x, float(0));
        const inS: N = select(row.lessThan(last), fluxS.element(min(i.add(n), int(nn - 1))).w, float(0));
        const inN: N = select(row.greaterThan(0), fluxS.element(max(i.sub(n), int(0))).z, float(0));
        const bnd: N = select(col.lessThan(last), float(0), f.x).add(select(col.greaterThan(0), float(0), f.y))
          .add(select(row.lessThan(last), float(0), f.z)).add(select(row.greaterThan(0), float(0), f.w));
        const net: N = inE.add(inW).add(inS).add(inN).sub(f.x.add(f.y).add(f.z).add(f.w));
        const dn: N = d0.add(net.mul(dt).div(A)).toVar('swDn');
        const gain: N = max(float(0), dn.negate());
        dn.assign(max(dn, float(0)));
        const r: N = this.uRain.mul(S.y).mul(dt);
        dn.addAssign(r);
        const inf: N = min(dn, S.x.mul(this.uInfilScale).mul(dt)).toVar('swInf');
        dn.subAssign(inf);
        const ev: N = min(dn, this.uEvap.mul(dt)).toVar('swEv');
        dn.subAssign(ev);
        // sheet drainage: a STAGNANT sheet above the cell's retention leaves by the drains the grid cannot draw
        const fadeK: N = float(1).sub(smoothstep(float(sLo), float(sHi), slopeS.element(i)));
        const ro: N = min(max(dn.sub(S.w), float(0)), this.uSheetDrain.mul(dt).mul(fadeK)).toVar('swRo');
        dn.subAssign(ro);
        const dr: N = select(S.z.greaterThan(1.5).and(S.z.lessThan(2.5)), dn, float(0)).toVar('swDr');
        dn.subAssign(dr);
        depthS.element(i).assign(dn);
        ledS.element(i).assign(ledS.element(i).add(vec4(r, inf, ev, dr)));
        led2S.element(i).assign(led2S.element(i).add(vec4(bnd.mul(dt).div(A), gain, 0, ro)));
      });
    })().compute(nn);

    // ── K3: the look — (depth, the water level relative to refY, the surface slope over WET neighbours, valid) ──
    this.kLook = Fn(() => {
      const i: N = int(instanceIndex);
      const col: N = i.mod(n), row: N = i.div(n);
      const d: N = depthS.element(i);
      const eta: N = bedS.element(i).add(d);
      const wetEta = (j: N, ok: N): N => {
        const dj: N = depthS.element(j);
        return select(ok.and(dj.greaterThan(1e-4)), bedS.element(j).add(dj), eta);
      };
      const jE: N = min(i.add(1), int(nn - 1)), jW: N = max(i.sub(1), int(0));
      const jS: N = min(i.add(n), int(nn - 1)), jN: N = max(i.sub(n), int(0));
      const gx: N = wetEta(jE, col.lessThan(last)).sub(wetEta(jW, col.greaterThan(0))).mul(0.5 / this.cell);
      const gz: N = wetEta(jS, row.lessThan(last)).sub(wetEta(jN, row.greaterThan(0))).mul(0.5 / this.cell);
      const slope: N = max(abs(gx), abs(gz));
      const valid: N = select(srcS.element(i).z.greaterThan(2.5), float(0), float(1)); // a pending cell: the renderer's fallback
      lookS.element(i).assign(vec4(d, eta, slope, valid));
    })().compute(nn);

    // ── the whole-cell shift: gather (new cells), ledger what leaves (old cells), copy back ──
    const uS: N = this.uShift;
    this.kShiftGather = Fn(() => {
      const i: N = int(instanceIndex);
      const col: N = i.mod(n), row: N = i.div(n);
      const sc: N = col.add(int(uS.x)), sr: N = row.add(int(uS.y));
      const ok: N = sc.greaterThanEqual(0).and(sc.lessThan(n)).and(sr.greaterThanEqual(0)).and(sr.lessThan(n));
      const j: N = min(max(sr, int(0)), int(last)).mul(n).add(min(max(sc, int(0)), int(last)));
      tmpDS.element(i).assign(select(ok, depthS.element(j), float(0)));
      tmpFS.element(i).assign(select(ok, fluxS.element(j), vec4(0, 0, 0, 0)));
    })().compute(nn);
    this.kShiftLost = Fn(() => {
      const i: N = int(instanceIndex);
      const col: N = i.mod(n), row: N = i.div(n);
      const nc: N = col.sub(int(uS.x)), nr: N = row.sub(int(uS.y));
      const gone: N = nc.lessThan(0).or(nc.greaterThanEqual(n)).or(nr.lessThan(0)).or(nr.greaterThanEqual(n));
      If(gone, () => { led2S.element(i).assign(led2S.element(i).add(vec4(0, 0, depthS.element(i), 0))); });
    })().compute(nn);
    this.kShiftCopy = Fn(() => {
      const i: N = int(instanceIndex);
      depthS.element(i).assign(tmpDS.element(i));
      fluxS.element(i).assign(tmpFS.element(i));
    })().compute(nn);
    this.kLedgerReset = Fn(() => {
      const i: N = int(instanceIndex);
      ledS.element(i).assign(vec4(0, 0, 0, 0));
      led2S.element(i).assign(vec4(0, 0, 0, 0));
    })().compute(nn);
    this.kClear = Fn(() => {
      const i: N = int(instanceIndex);
      depthS.element(i).assign(float(0));
      fluxS.element(i).assign(vec4(0, 0, 0, 0));
      lookS.element(i).assign(vec4(0, 0, 0, 0));
    })().compute(nn);
  }

  /** The look buffer as a read-only storage node (a fragment shader indexes `.element(row·n + col)`). */
  lookNode(): N { return storage(this.lookAttr, 'vec4', this.n * this.n).toReadOnly(); }

  /**
   * A per-cell exposure kernel from the owner's TSL function (e.g. Atlas's roof map): `exposureAt(renderPos)` for
   * each cell's ground point — (originRender.x + col·cell, bed + refY, originRender.y + row·cell). Run with `refreshMask`.
   */
  setMaskFunction(exposureAt: (p: N) => N, uOriginRender: N, uRefY: N): void {
    const n = this.n, nn = n * n;
    const srcS: N = storage(this.srcAttr, 'vec4', nn);
    const bedS: N = storage(this.bedAttr, 'float', nn).toReadOnly();
    const cell = this.cell;
    this.maskKernel = Fn(() => {
      const i: N = int(instanceIndex);
      const col: N = i.mod(n), row: N = i.div(n);
      const o: N = uOriginRender;
      const p: N = vec3(o.x.add(float(col).mul(cell)), bedS.element(i).add(uRefY), o.y.add(float(row).mul(cell)));
      const S: N = srcS.element(i);
      srcS.element(i).assign(vec4(S.x, exposureAt(p), S.z, S.w));
    })().compute(nn);
  }

  /** Run the exposure kernel (after the roof map re-bakes, or on a recentre). */
  refreshMask(r: ComputeRenderer): boolean {
    if (!this.maskKernel) return false;
    this.flushInputs();
    r.compute(this.maskKernel);
    return true;
  }

  /** The owner changed `bed` / `src` / `edge` — upload before the next dispatch. */
  markInputsDirty(): void { this.inputsDirty = true; }

  private flushInputs(): void {
    if (!this.inputsDirty) return;
    this.bedAttr.needsUpdate = true;
    this.srcAttr.needsUpdate = true;
    this.edgeAttr.needsUpdate = true;
    this.inputsDirty = false;
    this.stats.uploads++;
  }

  /** How many substeps `dtS` of real time asks for (the debt carried, clamped, capped) — the pure scheduler. */
  plan(dtS: number): number {
    if (!(dtS > 0)) return 0;
    this.debt = Math.min(this.debt + dtS, this.maxDebt);
    let k = Math.floor(this.debt / this.dt);
    if (k > this.maxSubsteps) { this.stats.dropped += k - this.maxSubsteps; k = this.maxSubsteps; }
    this.debt -= k * this.dt;
    if (this.debt >= this.dt) this.debt %= this.dt;
    return k;
  }

  /** Advance by `dtS` real seconds: [K1, K2] × k then K3, one compute list. Returns the substeps run. */
  advance(r: ComputeRenderer, dtS: number): number {
    const k = this.plan(dtS);
    const list: N[] = [];
    // an upload rewrites every cell's exposure with the CPU's copy — the mask kernel runs first whenever one happened
    if (this.inputsDirty) { this.flushInputs(); if (this.maskKernel) list.push(this.maskKernel); }
    if (k <= 0) { if (list.length) r.compute(list); return 0; }
    for (let s = 0; s < k; s++) list.push(this.kFlux, this.kDepth);
    list.push(this.kLook);
    r.compute(list);
    this.stats.substeps += k; this.stats.frames++; this.stats.lastSubsteps = k;
    return k;
  }

  /** Exactly `k` substeps now (the bed's fast-forward / parity legs), then the look. */
  stepExactly(r: ComputeRenderer, k: number): void {
    const list: N[] = [];
    if (this.inputsDirty) { this.flushInputs(); if (this.maskKernel) list.push(this.maskKernel); }
    for (let s = 0; s < k; s++) list.push(this.kFlux, this.kDepth);
    list.push(this.kLook);
    r.compute(list);
    this.stats.substeps += k;
  }

  /**
   * THE WARM (shader gaps R6): three creates a compute pipeline synchronously on its first dispatch — prime the kernels
   * one a call (the owner calls this once a frame until it returns true) so no single frame pays for all of them.
   */
  prime(r: ComputeRenderer): boolean {
    const stage = this.stats.primed;
    if (stage >= PRIME_DONE) return true;
    this.flushInputs();
    this.uShift.value.set(0, 0); // a zero shift gathers the state onto itself — harmless
    switch (stage) {
      case 0: r.compute(this.kClear); break;
      case 1: r.compute(this.kLedgerReset); break;
      case 2: r.compute(this.kLook); break;
      case 3: r.compute(this.kShiftGather); break;
      case 4: r.compute(this.kShiftLost); break;
      case 5: r.compute(this.kShiftCopy); break;
      case PRIME_MASK: if (this.maskKernel) r.compute(this.maskKernel); break;
      case 7: r.compute(this.kFlux); break; // on the cleared state: every flux stays 0
      case 8: { // K2 with no sources: nothing moves
        const rain = this.uRain.value, ev = this.uEvap.value;
        this.uRain.value = 0; this.uEvap.value = 0; r.compute(this.kDepth); this.uRain.value = rain; this.uEvap.value = ev;
        break;
      }
      default: r.compute([this.kClear, this.kLedgerReset]); break;
    }
    this.stats.primed++;
    return this.stats.primed >= PRIME_DONE;
  }

  /** Move the window by (dc, dr) whole cells (see the header). The owner then rewrites bed / src / edge for the new window. */
  shift(r: ComputeRenderer, dc: number, dr: number): void {
    if (dc === 0 && dr === 0) return;
    this.uShift.value.set(dc, dr);
    r.compute([this.kShiftGather, this.kShiftLost, this.kShiftCopy]);
    this.stats.shifts++;
  }

  /** Empty the patch (a teleport, a world change) — the ledger keeps what it had. */
  clear(r: ComputeRenderer): void { r.compute(this.kClear); }
  /** Zero the ledger. */
  resetLedger(r: ComputeRenderer): void { r.compute(this.kLedgerReset); }

  /** Read the depth field and the ledger back (async; a few hundred KB) and sum them in doubles. */
  async readback(r: ComputeRenderer): Promise<ShallowWaterGpuReadback> {
    const [dBuf, lBuf, l2Buf] = await Promise.all([r.getArrayBufferAsync(this.depthAttr), r.getArrayBufferAsync(this.ledgerAttr), r.getArrayBufferAsync(this.ledger2Attr)]);
    const depth = new Float32Array(dBuf.slice(0));
    const L = new Float32Array(lBuf), L2 = new Float32Array(l2Buf);
    const A = this.cell * this.cell;
    let vol = 0, wet = 0, mx = 0;
    for (let i = 0; i < depth.length; i++) { const d = depth[i]; vol += d; if (d > 1e-4) wet++; if (d > mx) mx = d; }
    const led: ShallowWaterGpuLedger = { rain: 0, infiltration: 0, evaporation: 0, runoff: 0, drained: 0, boundary: 0, clampGain: 0, shiftedOut: 0 };
    for (let i = 0; i < depth.length; i++) {
      led.rain += L[i * 4]; led.infiltration += L[i * 4 + 1]; led.evaporation += L[i * 4 + 2]; led.drained += L[i * 4 + 3];
      led.boundary += L2[i * 4]; led.clampGain += L2[i * 4 + 1]; led.shiftedOut += L2[i * 4 + 2]; led.runoff += L2[i * 4 + 3];
    }
    for (const k of Object.keys(led) as (keyof ShallowWaterGpuLedger)[]) led[k] *= A;
    return { depth, volume: vol * A, wetCells: wet, maxDepth: mx, ledger: led };
  }

  dispose(): void {
    for (const k of [this.kFlux, this.kDepth, this.kLook, this.kShiftGather, this.kShiftLost, this.kShiftCopy, this.kLedgerReset, this.kClear, this.maskKernel]) {
      try { k?.dispose?.(); } catch { /* already gone */ }
    }
  }
}

// ── THE F32 EMULATOR — the kernels' arithmetic on the CPU in float32 (the vitest's bridge between the oracle and the GPU) ──

/** CPU state of an emulated patch (the same layout as the GPU buffers: src = infil, exposure, kind 0|1 solid|2 drain, retain). */
export interface EmulatedPatch {
  n: number; cell: number; dt: number; damping: number; viscosity: number; gravity: number; openEdges: boolean; sheetSlope: [number, number];
  bed: Float32Array; src: Float32Array; edge: Float32Array;
  depth: Float32Array; flux: Float32Array; slope: Float32Array;
  /** Doubles: rain, infiltration, evaporation, drained, boundary, clampGain, runoff. */
  ledger: Float64Array;
  rain: number; evap: number; infilScale: number; sheetDrain: number;
}

export function makeEmulatedPatch(n: number, cell: number, o: { damping?: number; viscosity?: number; gravity?: number; openEdges?: boolean; dt?: number; sheetSlope?: [number, number] } = {}): EmulatedPatch {
  const gravity = o.gravity ?? 9.81;
  return {
    n, cell, gravity, damping: o.damping ?? 0.5, viscosity: o.viscosity ?? 1e-5, openEdges: o.openEdges ?? true, sheetSlope: o.sheetSlope ?? [0.005, 0.02],
    dt: o.dt ?? patchStep(cell, gravity),
    bed: new Float32Array(n * n), src: new Float32Array(n * n * 4).map((_, k) => (k % 4 === 1 ? 1 : 0)), edge: new Float32Array(4 * n),
    depth: new Float32Array(n * n), flux: new Float32Array(n * n * 4), slope: new Float32Array(n * n), ledger: new Float64Array(7),
    rain: 0, evap: 0, infilScale: 1, sheetDrain: 0,
  };
}

const fr = Math.fround;
const sstep32 = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, fr(fr(x - a) / fr(b - a)))); return fr(fr(t * t) * fr(3 - fr(2 * t))); };

/** One substep of K1 + K2 exactly as the TSL computes it, every operation rounded to float32 (the ledger summed in doubles). */
export function emulateSubstep(p: EmulatedPatch): void {
  const { n, bed, src, edge, depth, flux, slope } = p;
  const A = fr(p.cell * p.cell);
  const pipeK = fr((p.gravity * p.cell * p.cell) / p.cell);
  const keep = fr(Math.max(0, 1 - p.damping * p.dt));
  const visc3 = fr(3 * p.dt * p.viscosity);
  const dt = fr(p.dt);
  const inv2c = fr(0.5 / p.cell);
  const last = n - 1;
  const solidK = (k: number): boolean => k > 0.5 && (k < 1.5 || k > 2.5);
  const nf = new Float32Array(flux.length);
  for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
    const i = row * n + col;
    if (solidK(src[i * 4 + 2])) { slope[i] = 0; continue; }
    const d = depth[i];
    const head = fr(bed[i] + d);
    const etaOf = (ok: boolean, j: number): number => (ok && depth[j] > 1e-4 && !solidK(src[j * 4 + 2]) ? fr(bed[j] + depth[j]) : head);
    const jE = Math.min(i + 1, n * n - 1), jW = Math.max(i - 1, 0), jS = Math.min(i + n, n * n - 1), jN = Math.max(i - n, 0);
    const gx = fr(fr(etaOf(col < last, jE) - etaOf(col > 0, jW)) * inv2c);
    const gz = fr(fr(etaOf(row < last, jS) - etaOf(row > 0, jN)) * inv2c);
    slope[i] = Math.max(Math.abs(gx), Math.abs(gz));
    const vis = visc3 > 0 ? fr(fr(d * d) / fr(fr(d * d) + visc3)) : 1;
    const f: number[] = [0, 0, 0, 0];
    for (let s = 0; s < 4; s++) {
      const inside = s === 0 ? col < last : s === 1 ? col > 0 : s === 2 ? row < last : row > 0;
      const nc = s === 0 ? Math.min(col + 1, last) : s === 1 ? Math.max(col - 1, 0) : col;
      const nr = s === 2 ? Math.min(row + 1, last) : s === 3 ? Math.max(row - 1, 0) : row;
      const j = nr * n + nc;
      const eIdx = s === 0 ? row : s === 1 ? row + n : s === 2 ? col + 2 * n : col + 3 * n;
      const headN = inside ? fr(bed[j] + depth[j]) : edge[eIdx];
      const raw = fr(Math.max(0, fr(fr(flux[i * 4 + s] * keep) + fr(fr(pipeK * fr(head - headN)) * dt))) * vis);
      f[s] = inside ? (solidK(src[j * 4 + 2]) ? 0 : raw) : (p.openEdges ? raw : 0);
    }
    const total = fr(fr(fr(f[0] + f[1]) + f[2]) + f[3]);
    const k = total > 0 ? Math.min(1, fr(fr(d * A) / fr(total * dt))) : 1;
    for (let s = 0; s < 4; s++) nf[i * 4 + s] = fr(f[s] * k);
  }
  flux.set(nf);
  const L = p.ledger;
  for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
    const i = row * n + col;
    const d0 = depth[i];
    const kind = src[i * 4 + 2];
    if (solidK(kind)) { L[3] += d0 * A; depth[i] = 0; continue; }
    const f0 = flux[i * 4], f1 = flux[i * 4 + 1], f2 = flux[i * 4 + 2], f3 = flux[i * 4 + 3];
    const inE = col < last ? flux[(i + 1) * 4 + 1] : 0;
    const inW = col > 0 ? flux[(i - 1) * 4] : 0;
    const inS = row < last ? flux[(i + n) * 4 + 3] : 0;
    const inN = row > 0 ? flux[(i - n) * 4 + 2] : 0;
    const bnd = fr(fr(fr((col < last ? 0 : f0) + (col > 0 ? 0 : f1)) + (row < last ? 0 : f2)) + (row > 0 ? 0 : f3));
    const net = fr(fr(fr(fr(inE + inW) + inS) + inN) - fr(fr(fr(f0 + f1) + f2) + f3));
    let dn = fr(d0 + fr(fr(net * dt) / A));
    const gain = Math.max(0, -dn);
    dn = Math.max(dn, 0);
    const r = fr(fr(p.rain * src[i * 4 + 1]) * dt);
    dn = fr(dn + r);
    const inf = Math.min(dn, fr(fr(src[i * 4] * p.infilScale) * dt));
    dn = fr(dn - inf);
    const ev = Math.min(dn, fr(p.evap * dt));
    dn = fr(dn - ev);
    const fadeK = fr(1 - sstep32(p.sheetSlope[0], p.sheetSlope[1], slope[i]));
    const ro = Math.min(Math.max(fr(dn - src[i * 4 + 3]), 0), fr(fr(p.sheetDrain * dt) * fadeK));
    dn = fr(dn - ro);
    const drn = kind > 1.5 && kind < 2.5 ? dn : 0;
    dn = fr(dn - drn);
    depth[i] = dn;
    L[0] += r * A; L[1] += inf * A; L[2] += ev * A; L[3] += drn * A; L[4] += fr(fr(bnd * dt) / A) * A; L[5] += gain * A; L[6] += ro * A;
  }
}
